import { useMemo } from "react";

/** Measures the rendered width of a string at a given font size, in px. */
export type MeasureTextWidthPx = (text: string, fontSizePx: number) => number;

// Only used when a canvas 2D context is unavailable (jsdom, exotic browsers). A flat per-character
// average runs wide for text made of narrow glyphs and narrow for wide ones, so it is a fallback
// rather than a default.
const FALLBACK_CHAR_WIDTH_RATIO = 0.55;

export const estimateTextWidthPx: MeasureTextWidthPx = (text, fontSizePx) =>
  text.length * fontSizePx * FALLBACK_CHAR_WIDTH_RATIO;

/**
 * Measures real glyph widths through one reused canvas, so the bar chart's rotated axis labels
 * get margins that fit the actual text instead of a per-character average. Results are cached
 * per font size and string. Kept next to its only consumer, not in shared/hooks.
 *
 * Falls back to {@link estimateTextWidthPx} when no 2D context is available.
 */
export const createTextMeasurer = (fontFamily: string): MeasureTextWidthPx => {
  let context: CanvasRenderingContext2D | null = null;
  try {
    context = document.createElement("canvas").getContext("2d");
  } catch {
    context = null;
  }
  if (!context) {
    return estimateTextWidthPx;
  }

  const measuringContext = context;
  const cache = new Map<string, number>();
  return (text, fontSizePx) => {
    const key = `${fontSizePx}|${text}`;
    const cached = cache.get(key);
    if (cached !== undefined) {
      return cached;
    }
    measuringContext.font = `${fontSizePx}px ${fontFamily}`;
    const measuredPx = measuringContext.measureText(text).width;
    // A stubbed canvas reports 0 for real text; fall back rather than under-reserve and clip.
    const widthPx = measuredPx > 0 || text.length === 0 ? measuredPx : estimateTextWidthPx(text, fontSizePx);
    cache.set(key, widthPx);
    return widthPx;
  };
};

/**
 * Builds a text measurer bound to the font family `element` actually renders in, so measurements
 * match what the user will see. Returns the estimate-only measurer until the element exists.
 */
export const useTextMeasurer = (element: HTMLElement | null): MeasureTextWidthPx =>
  useMemo(() => {
    if (!element) {
      return estimateTextWidthPx;
    }
    return createTextMeasurer(window.getComputedStyle(element).fontFamily);
  }, [element]);
